import React, { useEffect, useState } from "react";
import { fetchRoleSuggestions } from "../lib/api";

/**
 * RoleOpeningsEditor — Phase 36 form widget. Lets a team manager declare
 * which roles they're looking to fill. Free-form (any string up to 30
 * chars), but we surface a suggested taxonomy from `/api/roles?game=...`
 * to keep things consistent across teams.
 *
 * Props:
 *   value: list[string]
 *   onChange: fn(list[string])
 *   game?: string — used to bubble game-specific roles to the top
 *   testidPrefix?: string
 */
export function RoleOpeningsEditor({ value = [], onChange, game, testidPrefix = "openings" }) {
  const [suggestions, setSuggestions] = useState([]);
  const [draft, setDraft] = useState("");

  useEffect(() => {
    fetchRoleSuggestions(game).then(setSuggestions).catch(() => setSuggestions([]));
  }, [game]);

  const norm = (s) => (s || "").trim().toLowerCase().slice(0, 30);
  const add = (raw) => {
    const v = norm(raw);
    if (!v) return;
    if (value.includes(v)) return;
    if (value.length >= 12) return;
    onChange?.([...value, v]);
    setDraft("");
  };
  const remove = (r) => onChange?.(value.filter((x) => x !== r));

  return (
    <div data-testid={`${testidPrefix}-editor`} className="space-y-2">
      <p className="text-[10px] tracking-[3px] text-[#a3a3ac]">
        <i className="fas fa-people-group mr-1" />ROLE OPENINGS
        <span className="text-white/40 normal-case tracking-normal ml-2 text-[10px]">
          What positions are you scouting for? (max 12)
        </span>
      </p>

      {/* Selected chips */}
      <div className="flex flex-wrap gap-1.5" data-testid={`${testidPrefix}-current`}>
        {value.length === 0 ? (
          <span className="text-[11px] text-white/40">No openings declared — your team won't show in the role-seeking filter.</span>
        ) : value.map((r) => (
          <span
            key={r}
            data-testid={`${testidPrefix}-chip-${r}`}
            className="inline-flex items-center gap-1 text-[11px] font-bold px-2.5 py-1 rounded-full bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40"
          >
            {r}
            <button
              type="button"
              onClick={() => remove(r)}
              data-testid={`${testidPrefix}-remove-${r}`}
              className="ml-0.5 text-[#14b8a6]/70 hover:text-[#14b8a6]"
              aria-label={`Remove ${r}`}
            >
              <i className="fas fa-xmark" />
            </button>
          </span>
        ))}
      </div>

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {suggestions
            .filter((s) => !value.includes(s.slug))
            .slice(0, 12)
            .map((s) => (
              <button
                key={s.slug}
                type="button"
                onClick={() => add(s.slug)}
                data-testid={`${testidPrefix}-suggest-${s.slug}`}
                className="text-[10px] tracking-widest px-2 py-1 rounded-md border border-white/15 bg-black/30 text-white/70 hover:border-[#14b8a6]/40 hover:text-[#14b8a6]"
              >
                + {s.label}
              </button>
            ))}
        </div>
      )}

      {/* Custom add */}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(draft); } }}
          maxLength={30}
          placeholder="Custom role (e.g. shotcaller)"
          data-testid={`${testidPrefix}-input`}
          className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-1.5 text-[12px] text-white"
        />
        <button
          type="button"
          onClick={() => add(draft)}
          data-testid={`${testidPrefix}-add`}
          disabled={!draft.trim() || value.length >= 12}
          className="text-[10px] tracking-widest px-3 py-1.5 rounded-lg bg-[#14b8a6]/15 text-[#14b8a6] border border-[#14b8a6]/40 hover:bg-[#14b8a6]/25 disabled:opacity-50"
        >
          ADD
        </button>
      </div>
    </div>
  );
}

export default RoleOpeningsEditor;
